import { restoreKmlSource, type KmlSource } from '../shared/kml-source.js';
import { applyMarkerDetails } from '../shared/marker-details.js';
import { applyDrawingLegend, type DrawingLegendLink } from '../shared/drawing-legend.js';
import { copyDataLayer } from '../shared/data-layer-snapshot.js';
import { applyTravelTimeArea, travelTimeForPoints } from '../shared/travel-time-area.js';
import { applyDrawingGeographicSource, geographicSourceForPoints, type DrawingGeographicSource } from '../shared/drawing-geographic-source.js';
import type { TravelTimeArea } from '../shared/travel-time-area.js';
import type { DataLayer } from '../shared/data-layer.js';
import { normalizeStrokeOpacity, normalizeUnitOpacity, normalizePolygonOpacity } from '../shared/stroke-opacity.js';
import type { SchemaInput } from '../shared/schema-input.js';
import { customDrawingLayerId, type DrawingLayer } from '../shared/drawing-layer.js';
import { isFunction, isNumber, isObject, isString } from '../shared/runtime-values.js';
import { Coordinate } from './distance-math.js';
import { normalizeLineStyle, type LineStyle } from './stroke-style.js';
import { isLineOrArrowTool, normalizedLineCurveControls } from './line-geometry.js';
import { isMarkerIconName } from '../shared/marker-icon.js';
import { maxStrokeWidth } from '../shared/stroke-limits.js';
import { hasCompleteStrokePoints } from '../shared/stroke-points.js';
import type { MapImage } from '../types/app.js';
import { applyMapImageAction, isMapImageAction, revertMapImageAction } from './map-image-actions.js';

interface ScreenPoint {
  x: number;
  y: number;
}

type StrokeTool = 'freehand' | 'line' | 'arrow' | 'circle' | 'polygon' | 'marker' | 'text';

interface Stroke {
  kmlSource?: KmlSource;
  geographicSource?: DrawingGeographicSource;
  legend?: DrawingLegendLink;
  travelTime?: TravelTimeArea;
  id: string;
  points: Coordinate[];
  curveControls?: Array<Coordinate | null>;
  color: string;
  width: number;
  pressures?: number[];
  tool: StrokeTool;
  zoom: number;
  zoomScale: boolean;
  opacity?: number;
  fillColor?: string;
  fillOpacity?: number;
  outlineOpacity?: number;
  lineStyle?: LineStyle;
  geodesic?: boolean;
  text?: string;
  rotation?: number;
  markerIcon?: string;
  markerAddress?: string;
  markerNote?: string;
  markerLabelOffset?: number;
  layerId?: string;
}

export interface Frame {
  idsKey: string;
  center: ScreenPoint;
  width: number;
  height: number;
  rotation: number;
  resolution?: number;
  aabbOffset?: ScreenPoint;
}

export interface EditorSettings {
  mapEditor?: import('../shared/saved-map-editor.js').SavedMapEditor | null;
  serviceAreaConfig?: import('../shared/service-area-config.js').ServiceAreaConfig | null;
  serverMapTitle?: string | null;
  showLayerLegend?: boolean;
  zoomScalingEnabled: boolean;
  currentBasemap: string;
  currentColor: string;
  currentFillColor: string;
  currentWidth: number;
  currentOpacity: number;
  currentPolygonOutlineOpacity: number;
  currentFillOpacity: number;
  currentLineStyle: LineStyle;
  lineContinuationEnabled: boolean;
  lineContinuationStrokeId: string | null;
  markerLabel: string;
  currentMarkerIcon: string;
}

type MapSettings = Pick<EditorSettings, 'currentBasemap' | 'showLayerLegend' | 'serviceAreaConfig' | 'serverMapTitle' | 'mapEditor'>;
export type DrawingDefaults = Omit<EditorSettings, keyof MapSettings>;

interface EditorState {
  settings?: Partial<EditorSettings>;
  dataLayers?: DataLayer[];
  strokes: Stroke[];
  images?: MapImage[];
  selectionIds: Set<string>;
  selectionFrame: Frame | null;
  drawingLayers?: DrawingLayer[];
  activeDrawingLayerId?: string;
}

interface HistoryState {
  editorState: EditorState;
  historyStack: Action[];
  redoStack: Action[];
}

export type Action =
  | { type: 'batch'; actions: Action[]; selectionIdsAfter?: string[]; selectionFrameAfter?: Frame | null }
  | { type: 'update_editor_settings'; before: Partial<MapSettings>; after: Partial<MapSettings> }
  | { type: 'update_data_features'; before: DataLayer[]; after: DataLayer[]; strokeAction?: Exclude<Action, { type: 'update_data_features' }> }
  | { type: 'add_stroke'; stroke: Stroke }
  | { type: 'add_strokes'; strokes: Stroke[]; selectionIdsAfter?: string[]; selectionFrameAfter?: Frame | null }
  | { type: 'clear'; clearedStrokes: Stroke[]; replacementStrokes?: Stroke[]; selectionIdsBefore?: string[]; selectionFrameBefore?: Frame | null }
  | { type: 'update_strokes'; before: Stroke[]; after: Stroke[]; frameBefore?: Frame | null; frameAfter?: Frame | null }
  | { type: 'delete_strokes'; deleted: Array<{ index: number; stroke: Stroke }> }
  | { type: 'replace_strokes'; deleted: Array<{ index: number; stroke: Stroke }>; added: Stroke[]; insertAt?: number }
  | { type: 'update_drawing_layers'; before: DrawingLayerHistorySnapshot; after: DrawingLayerHistorySnapshot }
  | { type: 'add_image'; image: MapImage }
  | { type: 'update_image'; before: MapImage; after: MapImage; frameBefore?: Frame | null; frameAfter?: Frame | null }
  | { type: 'delete_image'; index: number; image: MapImage };

export interface DrawingLayerHistorySnapshot {
  drawingLayers: DrawingLayer[];
  activeDrawingLayerId: string;
  strokes: Stroke[];
  selectionIds: string[];
  selectionFrame: Frame | null;
}

interface NormalizeOptions {
  generateStrokeId?: () => string;
}

type StrokeInput = Partial<Omit<Stroke, 'tool' | 'zoomScale'> & {
  tool: unknown;
  zoomScale: unknown;
}>;

const DEFAULT_STROKE_COLOR = '#5b4bff';
const DEFAULT_STROKE_WIDTH = 4;
const DEFAULT_STROKE_TOOL = 'freehand';
const DEFAULT_STROKE_ZOOM = 13;
const VALID_STROKE_TOOLS = new Set<StrokeTool>(['freehand', 'line', 'arrow', 'circle', 'polygon', 'marker', 'text']);

function isFinitePoint(point: SchemaInput): point is Coordinate {
  return !!point
    && isObject(point)
    // SAFETY: The local owner contract defines this value with the stated concrete type.
    && Number.isFinite((/* SAFETY: The adjacent boundary checks validate every field that this code reads. */ point as Coordinate).lat)
    // SAFETY: The local owner contract defines this value with the stated concrete type.
    && Number.isFinite((/* SAFETY: The adjacent boundary checks validate every field that this code reads. */ point as Coordinate).lng);
}

function normalizeStrokeColor(color: SchemaInput): string {
  return isString(color) && /^#[0-9a-fA-F]{6}$/.test(color)
    ? color
    : DEFAULT_STROKE_COLOR;
}

function normalizeStrokeWidth(width: SchemaInput, tool: StrokeTool): number {
  return isNumber(width) && Number.isFinite(width)
    ? Math.max(1, Math.min(maxStrokeWidth(tool), Math.round(width)))
    : DEFAULT_STROKE_WIDTH;
}

function normalizeStrokeTool(tool: SchemaInput): StrokeTool {
  // SAFETY: The adjacent allowlist, normalizer, or equality check limits this value to the stated union.
  return isString(tool) && VALID_STROKE_TOOLS.has(tool as StrokeTool) ? tool as StrokeTool : DEFAULT_STROKE_TOOL;
}

function normalizeStrokeZoom(zoom: SchemaInput): number {
  return isNumber(zoom) && Number.isFinite(zoom)
    ? Math.max(0, Math.min(24, zoom))
    : DEFAULT_STROKE_ZOOM;
}

function normalizeZoomScale(zoomScale: SchemaInput): boolean {
  return zoomScale === false || zoomScale === 0 ? false : true;
}

function normalizePressures(value: SchemaInput, pointCount: number, tool: StrokeTool): number[] | undefined {
  if (tool !== 'freehand' || !Array.isArray(value) || value.length !== pointCount) return undefined;
  const pressures = value.map((pressure) => isNumber(pressure) && Number.isFinite(pressure) ? Math.max(0, Math.min(1, pressure)) : 0.5);
  return pressures.some((pressure) => pressure !== 0.5) ? pressures : undefined;
}

export function clonePoint(point: Coordinate): Coordinate {
  return { lat: point.lat, lng: point.lng };
}

export function cloneStroke(stroke: Stroke): Stroke {
  const cloned: Stroke = {
    id: stroke.id,
    points: stroke.points.map(clonePoint),
    color: stroke.color,
    width: stroke.width,
    tool: stroke.tool,
    zoom: stroke.zoom,
    zoomScale: stroke.zoomScale
  };
  if (stroke.kmlSource) cloned.kmlSource = structuredClone(stroke.kmlSource);
  if (stroke.pressures !== undefined) cloned.pressures = [...stroke.pressures];
  if (stroke.curveControls !== undefined) cloned.curveControls = stroke.curveControls.map((control) => control ? clonePoint(control) : null);
  if (stroke.opacity !== undefined) cloned.opacity = normalizeStrokeOpacity(stroke.opacity);
  if (stroke.fillColor !== undefined) cloned.fillColor = normalizeStrokeColor(stroke.fillColor);
  if (stroke.fillOpacity !== undefined) cloned.fillOpacity = Math.max(0, Math.min(1, stroke.fillOpacity));
  if (stroke.outlineOpacity !== undefined) cloned.outlineOpacity = normalizeUnitOpacity(stroke.outlineOpacity);
  if (stroke.lineStyle !== undefined) cloned.lineStyle = normalizeLineStyle(stroke.lineStyle);
  if (stroke.geodesic === false) cloned.geodesic = false;
  if (stroke.text !== undefined) cloned.text = stroke.text;
  if (stroke.rotation !== undefined) cloned.rotation = stroke.rotation;
  if (stroke.markerAddress !== undefined) cloned.markerAddress = stroke.markerAddress;
  if (stroke.markerNote !== undefined) cloned.markerNote = stroke.markerNote;
  if (stroke.markerIcon !== undefined) cloned.markerIcon = stroke.markerIcon;
  if (stroke.markerLabelOffset !== undefined) cloned.markerLabelOffset = stroke.markerLabelOffset;
  if (stroke.layerId !== undefined) cloned.layerId = stroke.layerId;
  if (stroke.legend) cloned.legend = { ...stroke.legend };
  if (stroke.travelTime) cloned.travelTime = travelTimeForPoints(stroke.travelTime, stroke.points);
  if (stroke.geographicSource) cloned.geographicSource = geographicSourceForPoints(stroke.geographicSource, stroke.points);
  return cloned;
}

export function cloneFrame(frame: Frame | null): Frame | null {
  if (!frame) return null;
  return {
    ...frame,
    center: { ...frame.center }
  };
}

function applyDrawingLayerId(stroke: Stroke, value: SchemaInput): void {
  const layerId = customDrawingLayerId(value);
  if (layerId) stroke.layerId = layerId;
}

function applyLineCurveControls(normalized: Stroke, stroke: StrokeInput): void {
  if (!isLineOrArrowTool(normalized.tool)) return;
  const curveControls = normalizedLineCurveControls(stroke.curveControls, normalized.points.length);
  if (curveControls) normalized.curveControls = curveControls;
}

function applyPolygonOutlineOpacity(normalized: Stroke, stroke: StrokeInput): void {
  if (normalized.tool === 'polygon' && stroke.outlineOpacity !== undefined) normalized.outlineOpacity = normalizeUnitOpacity(stroke.outlineOpacity);
}

export function normalizeStrokeDraft(stroke: StrokeInput, { generateStrokeId }: NormalizeOptions = {}): Stroke {
  const tool = normalizeStrokeTool(stroke.tool);
  const normalized: Stroke = {
    id: stroke.id || (isFunction(generateStrokeId) ? generateStrokeId() : ''),
    points: Array.isArray(stroke.points) ? stroke.points.filter(isFinitePoint).map(clonePoint) : [],
    color: normalizeStrokeColor(stroke.color),
    width: normalizeStrokeWidth(stroke.width, tool),
    tool,
    zoom: normalizeStrokeZoom(stroke.zoom),
    zoomScale: normalizeZoomScale(stroke.zoomScale)
  };
  restoreKmlSource(normalized, stroke.kmlSource);
  applyTravelTimeArea(normalized, stroke.travelTime);
  applyDrawingGeographicSource(normalized, stroke.geographicSource);
  const pressures = normalizePressures(stroke.pressures, normalized.points.length, tool);
  if (pressures) normalized.pressures = pressures;
  applyLineCurveControls(normalized, stroke);
  if (stroke.opacity !== undefined) normalized.opacity = normalizeStrokeOpacity(stroke.opacity);
  if (stroke.fillColor !== undefined) normalized.fillColor = normalizeStrokeColor(stroke.fillColor);
  if (isNumber(stroke.fillOpacity) && Number.isFinite(stroke.fillOpacity)) normalized.fillOpacity = Math.max(0, Math.min(1, stroke.fillOpacity));
  applyPolygonOutlineOpacity(normalized, stroke);
  if (stroke.lineStyle !== undefined) normalized.lineStyle = normalizeLineStyle(stroke.lineStyle);
  if (stroke.geodesic === false) normalized.geodesic = false;
  if (stroke.text !== undefined) normalized.text = stroke.text;
  if (stroke.rotation !== undefined && Number.isFinite(stroke.rotation)) normalized.rotation = stroke.rotation;
  applyMarkerDetails(normalized, stroke.markerAddress, stroke.markerNote);
  if (tool === 'marker' && isMarkerIconName(stroke.markerIcon)) normalized.markerIcon = stroke.markerIcon;
  if (tool === 'text' && isNumber(stroke.markerLabelOffset) && Number.isFinite(stroke.markerLabelOffset)) {
    normalized.markerLabelOffset = Math.max(0, Math.min(200, stroke.markerLabelOffset));
  }
  applyDrawingLayerId(normalized, stroke.layerId);
  applyDrawingLegend(normalized, stroke.legend);
  normalizePolygonOpacity(normalized);
  return normalized;
}

export function normalizeStroke(stroke: StrokeInput, options: NormalizeOptions = {}): Stroke {
  const normalized = normalizeStrokeDraft(stroke, options);
  if (!hasCompleteStrokePoints(normalized.tool, normalized.points)) throw new Error(`Incomplete ${normalized.tool} drawing.`);
  return normalized;
}

export function getSelectionIdsKey(ids: Set<string>): string {
  return Array.from(ids).sort().join('|');
}

function syncSelectionIds(selectionIds: Set<string>, strokes: Stroke[], images: MapImage[] = []): Set<string> {
  const liveIds = new Set([...strokes.map((stroke) => stroke.id), ...images.filter(image => !image.hidden && !image.locked).map(image => image.id)]);
  const nextIds = new Set<string>();
  selectionIds.forEach((id) => {
    if (liveIds.has(id)) nextIds.add(id);
  });
  return nextIds;
}

function normalizeStrokes(strokes: StrokeInput[], options: NormalizeOptions): Stroke[] {
  return strokes.map((stroke) => normalizeStroke(stroke, options));
}

function replaceStrokes(strokes: Stroke[], replacements: StrokeInput[], options: NormalizeOptions): Stroke[] {
  const byId = new Map(replacements.map((stroke) => [stroke.id, normalizeStroke(stroke, options)]));
  return strokes.map((stroke) => byId.get(stroke.id) || stroke);
}

function restoreDeletedStrokes(strokes: Stroke[], entries: Array<{ index: number; stroke: StrokeInput }>, options: NormalizeOptions): Stroke[] {
  const nextStrokes = strokes.map(cloneStroke);
  entries
    .slice()
    .sort((a, b) => a.index - b.index)
    .forEach((entry) => {
      const insertAt = Math.max(0, Math.min(entry.index, nextStrokes.length));
      nextStrokes.splice(insertAt, 0, normalizeStroke(entry.stroke, options));
    });
  return nextStrokes;
}

export function applyActionToEditorState(state: EditorState, action: Action, options: NormalizeOptions = {}): EditorState {
  if (action.type === 'batch') {
    const next = action.actions.reduce((current, child) => applyActionToEditorState(current, child, options), state);
    return action.selectionIdsAfter ? { ...next, selectionIds: new Set(action.selectionIdsAfter), selectionFrame: cloneFrame(action.selectionFrameAfter ?? null) } : next;
  }
  if (action.type === 'update_editor_settings') return { ...state, settings: { ...state.settings, ...action.after } };
  if (action.type === 'update_data_features') {
    const next = action.strokeAction ? applyActionToEditorState(state, action.strokeAction, options) : state;
    return restoreDataLayers(next, action.after, action.before);
  }
  if (isMapImageAction(action)) return applyImageActionToEditorState(state, action);
  return applyDrawingActionToEditorState(state, action, options);
}

function applyDrawingActionToEditorState(state: EditorState, action: Exclude<Action, { type: 'update_data_features' }>, options: NormalizeOptions): EditorState {
  let strokes = state.strokes.map(cloneStroke);
  let selectionIds = new Set(state.selectionIds);
  let selectionFrame = cloneFrame(state.selectionFrame);

  if (action.type === 'update_drawing_layers') {
    const snapshot = action.after;
    strokes = normalizeStrokes(snapshot.strokes, options);
    selectionIds = syncSelectionIds(new Set(snapshot.selectionIds), strokes, state.images);
    selectionFrame = selectionIds.size > 0 ? cloneFrame(snapshot.selectionFrame) : null;
    return {
      ...state,
      strokes,
      selectionIds,
      selectionFrame,
      drawingLayers: snapshot.drawingLayers.map((layer) => ({ ...layer })),
      activeDrawingLayerId: snapshot.activeDrawingLayerId,
    };
  }
  if (action.type === 'add_stroke') {
    strokes.push(normalizeStroke(action.stroke, options));
  } else if (action.type === 'add_strokes') {
    const added = normalizeStrokes(action.strokes, options);
    strokes.push(...added);
    if (action.selectionIdsAfter) {
      selectionIds = syncSelectionIds(new Set(action.selectionIdsAfter), strokes, state.images);
      selectionFrame = selectionIds.size > 0 ? cloneFrame(action.selectionFrameAfter || null) : null;
    }
  } else if (action.type === 'clear') {
    strokes = normalizeStrokes(action.replacementStrokes || [], options);
    selectionIds = new Set();
    selectionFrame = null;
  } else if (action.type === 'update_strokes') {
    strokes = replaceStrokes(strokes, action.after, options);
    selectionIds = syncSelectionIds(selectionIds, strokes, state.images);
    if (selectionIds.size === 0) {
      selectionFrame = null;
    } else if (
      action.frameAfter &&
      getSelectionIdsKey(selectionIds) === getSelectionIdsKey(new Set(action.after.map((stroke) => stroke.id)))
    ) {
      selectionFrame = cloneFrame(action.frameAfter);
    }
  } else if (action.type === 'delete_strokes') {
    const deletedIds = new Set(action.deleted.map((entry) => entry.stroke.id));
    strokes = strokes.filter((stroke) => !deletedIds.has(stroke.id));
    selectionIds = syncSelectionIds(selectionIds, strokes, state.images);
    if (selectionIds.size === 0) selectionFrame = null;
  } else if (action.type === 'replace_strokes') {
    const deletedIds = new Set(action.deleted.map((entry) => entry.stroke.id));
    strokes = strokes.filter((stroke) => !deletedIds.has(stroke.id));
    strokes.splice(action.insertAt ?? strokes.length, 0, ...normalizeStrokes(action.added, options));
    selectionIds = syncSelectionIds(selectionIds, strokes, state.images);
    if (selectionIds.size === 0) selectionFrame = null;
  }

  return { ...state, strokes, selectionIds, selectionFrame };
}

function applyImageActionToEditorState(state: EditorState, action: Extract<Action, { type: 'add_image' | 'update_image' | 'delete_image' }>): EditorState {
  const images = applyMapImageAction(state.images ?? [], action);
  const liveIds = new Set([...state.strokes.map((stroke) => stroke.id), ...images.filter((image) => !image.hidden).map((image) => image.id)]);
  const selectionIds = new Set([...state.selectionIds].filter((id) => liveIds.has(id)));
  const selectionFrame = action.type === 'update_image'
    ? cloneFrame(action.frameAfter ?? null)
    : selectionIds.size > 0 ? cloneFrame(state.selectionFrame) : null;
  return { ...state, images, selectionIds, selectionFrame };
}

export function revertActionInEditorState(state: EditorState, action: Action, options: NormalizeOptions = {}): EditorState {
  if (action.type === 'batch') return [...action.actions].reverse().reduce((current, child) => revertActionInEditorState(current, child, options), state);
  if (action.type === 'update_editor_settings') return { ...state, settings: { ...state.settings, ...action.before } };
  if (action.type === 'update_data_features') {
    const next = action.strokeAction ? revertActionInEditorState(state, action.strokeAction, options) : state;
    return restoreDataLayers(next, action.before, action.after);
  }
  if (action.type === 'update_drawing_layers') return restoreDrawingLayerSnapshot(state, action.before, options);
  if (isMapImageAction(action)) {
    const images = revertMapImageAction(state.images ?? [], action);
    const liveIds = new Set([...state.strokes.map((stroke) => stroke.id), ...images.filter((image) => !image.hidden).map((image) => image.id)]);
    const selectionIds = new Set([...state.selectionIds].filter((id) => liveIds.has(id)));
    const selectionFrame = action.type === 'update_image'
      ? cloneFrame(action.frameBefore ?? null)
      : selectionIds.size > 0 ? cloneFrame(state.selectionFrame) : null;
    return { ...state, images, selectionIds, selectionFrame };
  }
  return revertStrokeActionInEditorState(state, action, options);
}

function restoreDataLayers(state: EditorState, replacements: DataLayer[], previous: DataLayer[]): EditorState {
  const touched = new Set([...previous, ...replacements].map((layer) => layer.id));
  const current = new Map((state.dataLayers ?? []).map((layer) => [layer.id, layer]));
  const dataLayers = (state.dataLayers ?? []).filter((layer) => !touched.has(layer.id));
  for (const replacement of replacements) {
    const existing = current.get(replacement.id);
    dataLayers.push({ ...copyDataLayer(replacement), persisted: existing?.persisted ?? replacement.persisted, saved: false });
  }
  dataLayers.sort((a, b) => a.position - b.position);
  return { ...state, dataLayers };
}

function restoreDrawingLayerSnapshot(state: EditorState, snapshot: DrawingLayerHistorySnapshot, options: NormalizeOptions): EditorState {
  const strokes = normalizeStrokes(snapshot.strokes, options);
  const selectionIds = syncSelectionIds(new Set(snapshot.selectionIds), strokes, state.images);
  return {
    ...state,
    strokes,
    selectionIds,
    selectionFrame: selectionIds.size > 0 ? cloneFrame(snapshot.selectionFrame) : null,
    drawingLayers: snapshot.drawingLayers.map((layer) => ({ ...layer })),
    activeDrawingLayerId: snapshot.activeDrawingLayerId,
  };
}

function revertStrokeActionInEditorState(state: EditorState, action: Exclude<Action, { type: 'update_drawing_layers' | 'update_data_features' }>, options: NormalizeOptions): EditorState {
  let strokes = state.strokes.map(cloneStroke);
  let selectionIds = new Set(state.selectionIds);
  let selectionFrame = cloneFrame(state.selectionFrame);
  if (action.type === 'add_stroke') {
    strokes = strokes.filter((stroke) => stroke.id !== action.stroke.id);
    selectionIds = syncSelectionIds(selectionIds, strokes, state.images);
    if (selectionIds.size === 0) selectionFrame = null;
  } else if (action.type === 'add_strokes') {
    const addedIds = new Set(action.strokes.map((stroke) => stroke.id));
    strokes = strokes.filter((stroke) => !addedIds.has(stroke.id));
    selectionIds = syncSelectionIds(selectionIds, strokes, state.images);
    if (selectionIds.size === 0) selectionFrame = null;
  } else if (action.type === 'clear') {
    strokes = normalizeStrokes(action.clearedStrokes, options);
    if (action.selectionIdsBefore) {
      selectionIds = syncSelectionIds(new Set(action.selectionIdsBefore), strokes, state.images);
      selectionFrame = cloneFrame(action.selectionFrameBefore || null);
      if (selectionIds.size === 0) selectionFrame = null;
    } else {
      selectionIds = syncSelectionIds(selectionIds, strokes, state.images);
      if (selectionIds.size === 0) selectionFrame = null;
    }
  } else if (action.type === 'update_strokes') {
    strokes = replaceStrokes(strokes, action.before, options);
    selectionIds = syncSelectionIds(selectionIds, strokes, state.images);
    if (selectionIds.size === 0) {
      selectionFrame = null;
    } else if (
      action.frameBefore &&
      getSelectionIdsKey(selectionIds) === getSelectionIdsKey(new Set(action.before.map((stroke) => stroke.id)))
    ) {
      selectionFrame = cloneFrame(action.frameBefore);
    }
  } else if (action.type === 'delete_strokes') {
    strokes = restoreDeletedStrokes(strokes, action.deleted, options);
    selectionIds = syncSelectionIds(selectionIds, strokes, state.images);
    if (selectionIds.size === 0) selectionFrame = null;
  } else if (action.type === 'replace_strokes') {
    const addedIds = new Set(action.added.map((stroke) => stroke.id));
    strokes = restoreDeletedStrokes(strokes.filter((stroke) => !addedIds.has(stroke.id)), action.deleted, options);
    selectionIds = syncSelectionIds(selectionIds, strokes, state.images);
    if (selectionIds.size === 0) selectionFrame = null;
  }

  return { ...state, strokes, selectionIds, selectionFrame };
}

export function undoEditorHistory(state: HistoryState, options: NormalizeOptions = {}): HistoryState {
  if (state.historyStack.length === 0) return state;
  const action = state.historyStack[state.historyStack.length - 1];
  const editorState = revertActionInEditorState(state.editorState, action, options);
  return {
    editorState,
    historyStack: state.historyStack.slice(0, -1),
    redoStack: [...state.redoStack, action]
  };
}

export function redoEditorHistory(state: HistoryState, options: NormalizeOptions = {}): HistoryState {
  if (state.redoStack.length === 0) return state;
  const action = state.redoStack[state.redoStack.length - 1];
  const editorState = applyActionToEditorState(state.editorState, action, options);
  return {
    editorState,
    historyStack: [...state.historyStack, action],
    redoStack: state.redoStack.slice(0, -1)
  };
}

interface TransformConfig {
  mode: 'move' | 'resize' | 'rotate';
  center: ScreenPoint;
  startPoint: ScreenPoint;
  eventPoint: ScreenPoint;
}

interface TransformResult {
  points: ScreenPoint[];
  angleDelta: number;
  scale: number;
  deltaX: number;
  deltaY: number;
}

export function transformScreenPoints(screenPoints: ScreenPoint[], { mode, center, startPoint, eventPoint }: TransformConfig): TransformResult {
  const deltaX = eventPoint.x - startPoint.x;
  const deltaY = eventPoint.y - startPoint.y;
  let angleDelta = 0;
  let scale = 1;

  if (mode === 'rotate') {
    const startAngle = Math.atan2(startPoint.y - center.y, startPoint.x - center.x);
    const currentAngle = Math.atan2(eventPoint.y - center.y, eventPoint.x - center.x);
    angleDelta = currentAngle - startAngle;
  } else if (mode === 'resize') {
    const startDistance = Math.hypot(startPoint.x - center.x, startPoint.y - center.y);
    const currentDistance = Math.hypot(eventPoint.x - center.x, eventPoint.y - center.y);
    scale = startDistance > 0 ? Math.max(0.15, currentDistance / startDistance) : 1;
  }

  const points = screenPoints.map((screenPoint) => {
    const offsetX = screenPoint.x - center.x;
    const offsetY = screenPoint.y - center.y;

    if (mode === 'move') {
      return { x: screenPoint.x + deltaX, y: screenPoint.y + deltaY };
    }

    if (mode === 'resize') {
      return {
        x: center.x + offsetX * scale,
        y: center.y + offsetY * scale
      };
    }

    const cos = Math.cos(angleDelta);
    const sin = Math.sin(angleDelta);
    return {
      x: center.x + offsetX * cos - offsetY * sin,
      y: center.y + offsetX * sin + offsetY * cos
    };
  });

  return { points, angleDelta, scale, deltaX, deltaY };
}
